import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Timestamp,
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useAuth } from '@/core/AuthContext';
import { db } from '@/core/firebase';
import { SectionCard } from '../../components/trainer/SectionCard';
import { Button } from '../../components/ui';
import { Paywall } from '../../components/Paywall';
import { QuickStartModal } from '../../components/scheduling/QuickStartModal';
import { usePremiumAccess } from '../../lib/usePremiumAccess';
import type { AssignmentStatus, WorkoutAssignment } from '../../types';

const VISIBLE_ASSIGNMENT_STATUSES: AssignmentStatus[] = [
  'pending',
  'reminded',
  'confirmed',
];

const getAssignmentWorkoutName = (
  assignment: WorkoutAssignment
) =>
  assignment.workoutTemplateName ||
  'Workout';

export function Train() {
  const { user, userData, loading } = useAuth();
  const [assignments, setAssignments] = useState<WorkoutAssignment[]>([]);
  const [showQuickStartModal, setShowQuickStartModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const navigate = useNavigate();
  const { isPremiumActive, refresh, shouldRequirePremium } = usePremiumAccess();

  const trainerId = userData?.trainerId || userData?.trainerProfileId;
  const aiTrainerId = userData?.aiTrainerId;
  const effectiveTrainerId = trainerId || aiTrainerId;

  useEffect(() => {
    // Wait for auth to finish loading to avoid permission-denied errors
    if (loading || !user) return;

    const assignmentsQuery = query(
      collection(db, 'workoutAssignments'),
      where('clientId', '==', user.uid)
    );

    const unsubAssignments = onSnapshot(assignmentsQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutAssignment)
      );
      setAssignments(docs);
    });

    return () => {
      unsubAssignments();
    };
  }, [user, loading]);

  const todayAssignments = assignments.filter((a) => {
    if (!VISIBLE_ASSIGNMENT_STATUSES.includes(a.status)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduled = (a.scheduledFor as Timestamp).toDate();
    scheduled.setHours(0, 0, 0, 0);
    return scheduled.getTime() === today.getTime();
  });

  const upcomingAssignments = assignments.filter((a) => {
    if (!VISIBLE_ASSIGNMENT_STATUSES.includes(a.status)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduled = (a.scheduledFor as Timestamp).toDate();
    scheduled.setHours(0, 0, 0, 0);
    return scheduled.getTime() > today.getTime();
  });

  const handlePremiumQuickStartAction = () => {
    if (shouldRequirePremium && !isPremiumActive) {
      setShowPaywall(true);
      return;
    }

    setShowQuickStartModal(true);
  };

  return (
    <>
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onPurchased={async () => {
          await refresh();
          setShowPaywall(false);
          setShowQuickStartModal(true);
        }}
      />
      {user && effectiveTrainerId && (
        <QuickStartModal
          isOpen={showQuickStartModal}
          onClose={() => setShowQuickStartModal(false)}
          onSessionCreated={(sessionId) => {
            setShowQuickStartModal(false);
            navigate(`/session/${sessionId}`);
          }}
          userId={user.uid}
          trainerId={effectiveTrainerId}
        />
      )}
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
                Quick Start
              </p>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                Today's Training
              </h1>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Start your workout and track your progress
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handlePremiumQuickStartAction}
              className="sm:w-auto"
              data-testid="quick-start-btn"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Quick Start
            </Button>
          </div>
        </div>

        {/* Today's Workouts */}
        <SectionCard
          eyebrow="Today"
          title="Scheduled for Today"
          action={
            <span className="text-xs text-gray-600 dark:text-slate-400">
              {todayAssignments.length} workout{todayAssignments.length !== 1 ? 's' : ''}
            </span>
          }
        >
          <div className="space-y-3">
            {todayAssignments.length === 0 && (
              <p className="text-gray-600 dark:text-slate-400 text-sm">
                No workouts scheduled for today. Check your upcoming schedule!
              </p>
            )}
            {todayAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-500/10 dark:to-accent-500/10"
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-primary-600 dark:text-primary-400 font-semibold">
                      TODAY'S WORKOUT
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {(assignment.scheduledFor as Timestamp)
                        .toDate()
                        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                      {getAssignmentWorkoutName(assignment)}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/client`)}
                    className="px-6 py-3 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white transition font-semibold text-sm shadow-lg shadow-primary-500/25"
                  >
                    Start Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Coming Up */}
        <SectionCard
          eyebrow="Upcoming"
          title="Next Workouts"
          action={
            <span className="text-xs text-gray-600 dark:text-slate-400">
              {upcomingAssignments.length} scheduled
            </span>
          }
        >
          <div className="space-y-3">
            {upcomingAssignments.length === 0 && (
              <p className="text-gray-600 dark:text-slate-400 text-sm">
                No upcoming workouts scheduled.
              </p>
            )}
            {upcomingAssignments
              .sort(
                (a, b) =>
                  (a.scheduledFor as Timestamp).toMillis() -
                  (b.scheduledFor as Timestamp).toMillis()
              )
              .slice(0, 3)
              .map((assignment) => (
              <div
                key={assignment.id}
                className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5"
              >
                <div className="flex justify-between items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {(assignment.scheduledFor as Timestamp)
                        .toDate()
                        .toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {(assignment.scheduledFor as Timestamp)
                        .toDate()
                        .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <span className="text-xs uppercase tracking-wide font-semibold px-2 py-1 rounded bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-slate-400">
                    Scheduled
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
      </div>
    </>
  );
}
